/**
 * Supabase取引先サービス
 * BusinessPartner、CompanyPartnerSelection、CompanyPartnerSelection管理を担当
 */

import { SupabaseClient } from '@supabase/supabase-js';
import logger from '../../utils/logger';
import { getSupabaseClient } from '../../utils/supabase';
import { BusinessPartner, CompanyPartnerSelection, Client } from '../../types';
import { DataServiceError, ErrorCodes } from '../dataService';
import { DemoDataFallbackService } from '../demoDataFallback';

/**
 * @deprecated このサービスは将来的に削除予定です。
 *
 * 現在、取引先データの取得にはSupabaseMasterDataService.getBusinessPartners()を使用してください。
 * MasterDataContextも同様にSupabaseMasterDataServiceを使用しています。
 *
 * Phase 2のリファクタリングで、このクラスは削除され、Repository patternベースの
 * 新しいアーキテクチャに置き換えられます。
 *
 * 使用箇所がある場合は、SupabaseMasterDataService経由での取得に移行してください。
 */

export class SupabasePartnerService {
  public supabase: SupabaseClient | null = null;
  public currentCompanyId: string;

  constructor() {
    this.currentCompanyId =
      import.meta.env.VITE_DEMO_MODE === 'true' ? 'e32a0499-6456-44ac-9b16-b0001098c259' : '';
    this.initializeSupabase();
  }

  private initializeSupabase() {
    try {
      this.supabase = getSupabaseClient();
      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }
      logger.info('✅ SupabasePartnerService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ SupabasePartnerService initialization failed:', error);
    }
  }

  private ensureSupabaseClient() {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
  }

  /**
   * テスト専用メソッド: Supabaseクライアントを設定
   * プロダクションコードでは使用しないこと
   * @param client - 設定するSupabaseクライアント（テスト用）
   */
  public setSupabaseClientForTest(client: SupabaseClient | null): void {
    this.supabase = client;
  }

  // =============================================================================
  // BusinessPartner関連メソッド
  // =============================================================================

  /**
   * 取引先一覧を取得
   * @returns 取引先一覧
   */
  async getBusinessPartners(): Promise<BusinessPartner[]> {
    logger.info('🎯 SupabasePartnerService: getBusinessPartners開始');
    this.ensureSupabaseClient();

    if (!this.supabase) {
      logger.warn('Supabaseが利用できないため、ダミーデータを返します');
      return DemoDataFallbackService.getBusinessPartners();
    }

    try {
      // business_partnersテーブルから取得（SUPER_ADMIN対応の条件分岐）
      let query = this.supabase.from('business_partners').select(`
        *,
        business_partner_locations (*)
      `);

      // 通常ユーザーの場合はcompany_idフィルタを適用（テスト環境ではスキップ）
      const inTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
      if (!inTest && this.currentCompanyId) {
        query = query.eq('company_id', this.currentCompanyId);
      }

      query = query.order('name');

      const { data: partners, error } = await query;

      if (error) {
        logger.error('BusinessPartners取得エラー:', error);
        throw new DataServiceError(
          `取引先データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const businessPartners = (partners || []).map((partner: any) => ({
        id: partner.id,
        name: partner.name,
        displayName: partner.display_name || undefined,
        type: partner.type,
        status: partner.status,
        dataSource: partner.data_source,
        category: partner.category,
        parentId: partner.parent_id || undefined,
        availableRoles:
          partner.available_roles && partner.available_roles.length > 0
            ? partner.available_roles
            : [],
        locations: (partner.business_partner_locations || []).map((loc: any) => ({
          id: loc.id,
          name: loc.name,
          address: loc.address,
          phone: loc.phone,
          contactPerson: loc.contact_person,
          email: loc.email || '',
          notes: loc.notes || '',
          isDefault: loc.is_default || false,
        })),
        defaultAddress: partner.default_address,
        defaultPhone: partner.default_phone,
        defaultContactPerson: partner.default_contact_person,
        defaultEmail: partner.default_email || '',
        createdAt: partner.created_at,
        updatedAt: partner.updated_at,
        notes: partner.notes || undefined,
        isSystemPreset: partner.is_system_preset || false,
        companyId: partner.company_id,
      }));

      // company_partner_selectionsで有効な取引先のみをフィルタリング（SUPER_ADMIN免除）
      let filteredBusinessPartners = businessPartners;

      if (!inTest && this.currentCompanyId) {
        // 通常ユーザーの場合はcompany_partner_selectionsでフィルタリング
        const { data: partnerSelections, error: selectionError } = await this.supabase
          .from('company_partner_selections')
          .select('business_partner_id')
          .eq('company_id', this.currentCompanyId)
          .eq('is_enabled', true);

        if (selectionError) {
          logger.warn('⚠️ company_partner_selectionsの取得に失敗しました:', selectionError);
          // エラー時は空配列を返す（セキュリティ重視）
          filteredBusinessPartners = [];
        } else {
          // selectionsがある場合のみフィルタリング
          if (partnerSelections && partnerSelections.length > 0) {
            const enabledPartnerIds = new Set(partnerSelections.map(ps => ps.business_partner_id));
            filteredBusinessPartners = businessPartners.filter(partner =>
              enabledPartnerIds.has(partner.id)
            );
            logger.info('📊 取引先フィルタリング結果:', {
              全取引先: businessPartners.length,
              有効取引先: filteredBusinessPartners.length,
              有効ID一覧: Array.from(enabledPartnerIds).slice(0, 5),
            });
          } else {
            // 有効な取引先が0件の場合も空配列を返す
            logger.info('⚠️ 有効な取引先設定が見つかりません。空配列を返します。');
            filteredBusinessPartners = [];
          }
        }
      }

      logger.info('✅ SupabasePartnerService: getBusinessPartners完了', {
        raw: partners?.length || 0,
        count: partners?.length || 0,
        filteredCount: filteredBusinessPartners.length,
        companyId: this.currentCompanyId,
        partnerNames: filteredBusinessPartners
          .slice(0, 3)
          .map(p => p.name)
          .join(', '),
      });

      logger.info('🚀 SupabasePartnerService: getBusinessPartners返却データ', {
        finalCount: filteredBusinessPartners.length,
        samplePartners: filteredBusinessPartners.slice(0, 2).map(p => ({ id: p.id, name: p.name })),
      });

      return filteredBusinessPartners;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: getBusinessPartners失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい取引先を作成
   * @param partnerData 取引先データ（IDなし）
   * @returns 作成された取引先データ
   */
  async createBusinessPartner(partnerData: Omit<BusinessPartner, 'id'>): Promise<BusinessPartner> {
    logger.info('🚀 SupabasePartnerService: createBusinessPartner開始:', partnerData.name);
    this.ensureSupabaseClient();

    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが利用できません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }

    try {
      // business_partnersテーブルに挿入
      const { data: partner, error: partnerError } = await this.supabase
        .from('business_partners')
        .insert([
          {
            name: partnerData.name,
            display_name: partnerData.displayName,
            type: partnerData.type,
            status: partnerData.status,
            data_source: partnerData.dataSource,
            category: partnerData.category,
            parent_id: partnerData.parentId,
            available_roles: partnerData.availableRoles,
            default_address: partnerData.defaultAddress,
            default_phone: partnerData.defaultPhone,
            default_contact_person: partnerData.defaultContactPerson,
            default_email: partnerData.defaultEmail,
            notes: partnerData.notes,
            is_system_preset: partnerData.isSystemPreset,
            company_id: partnerData.companyId,
          },
        ])
        .select()
        .single();

      if (partnerError) {
        throw new DataServiceError(
          `取引先の作成に失敗しました: ${partnerError.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          partnerError
        );
      }

      // ロケーション情報があれば追加
      if (partnerData.locations && partnerData.locations.length > 0) {
        logger.info('📍 取引先ロケーション追加:', partnerData.locations.length);
        const locationsData = partnerData.locations.map(loc => ({
          business_partner_id: partner.id,
          name: loc.name,
          address: loc.address,
          phone: loc.phone,
          contact_person: loc.contactPerson,
          email: loc.email,
          // notes: loc.notes, // Removed from BusinessPartnerLocation type
          is_default: loc.isDefault || false,
        }));

        const { error: locationError } = await this.supabase
          .from('business_partner_locations')
          .insert(locationsData);

        if (locationError) {
          logger.warn('⚠️ 取引先ロケーション追加に失敗:', locationError);
        }
      }

      const createdPartners = await this.getBusinessPartners();
      const createdPartner = createdPartners.find(p => p.id === partner.id);

      if (!createdPartner) {
        throw new DataServiceError('作成された取引先が見つかりません', ErrorCodes.UNKNOWN_ERROR);
      }

      logger.info(`✅ SupabasePartnerService: createBusinessPartner完了 - ${createdPartner.name}`);
      return createdPartner;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: createBusinessPartner実行エラー:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 取引先を更新
   * @param id 取引先ID
   * @param partnerData 更新データ
   * @returns 更新された取引先データ
   */
  async updateBusinessPartner(
    id: string,
    partnerData: Partial<BusinessPartner>
  ): Promise<BusinessPartner> {
    logger.info(`🔧 SupabasePartnerService: updateBusinessPartner開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが利用できません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }

    try {
      const { error } = await this.supabase
        .from('business_partners')
        .update({
          name: partnerData.name,
          display_name: partnerData.displayName,
          type: partnerData.type,
          status: partnerData.status,
          data_source: partnerData.dataSource,
          category: partnerData.category,
          parent_id: partnerData.parentId,
          available_roles: partnerData.availableRoles,
          default_address: partnerData.defaultAddress,
          default_phone: partnerData.defaultPhone,
          default_contact_person: partnerData.defaultContactPerson,
          default_email: partnerData.defaultEmail,
          notes: partnerData.notes,
          is_system_preset: partnerData.isSystemPreset,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `取引先の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      const updatedPartners = await this.getBusinessPartners();
      const updatedPartner = updatedPartners.find(p => p.id === id);

      if (!updatedPartner) {
        throw new DataServiceError('更新された取引先が見つかりません', ErrorCodes.UNKNOWN_ERROR);
      }

      logger.info(`✅ SupabasePartnerService: updateBusinessPartner完了 - ${updatedPartner.name}`);
      return updatedPartner;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: updateBusinessPartner失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 取引先を削除
   * @param id 取引先ID
   */
  async deleteBusinessPartner(id: string): Promise<void> {
    logger.info(`🗑️ SupabasePartnerService: deleteBusinessPartner開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('business_partners').delete().eq('id', id);

      if (error) {
        throw new DataServiceError(
          `取引先の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info(`✅ SupabasePartnerService: deleteBusinessPartner完了 - ${id}`);
    } catch (error) {
      logger.error('❌ SupabasePartnerService: deleteBusinessPartner失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先の削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // CompanyPartnerSelection関連メソッド
  // =============================================================================

  /**
   * 会社取引先選択設定を取得
   * @param companyId 会社ID
   * @returns 取引先選択設定一覧
   */
  async getCompanyPartnerSelections(companyId?: string): Promise<CompanyPartnerSelection[]> {
    const targetCompanyId = companyId || this.currentCompanyId;
    logger.info(
      `📋 SupabasePartnerService: getCompanyPartnerSelections開始 - companyId: ${targetCompanyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data: selections, error } = await this.supabase!.from('company_partner_selections')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('CompanyPartnerSelections取得エラー:', error);
        throw new DataServiceError(
          `取引先選択設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const partnerSelections = (selections || []).map((selection: any) => ({
        id: selection.id,
        companyId: selection.company_id,
        businessPartnerId: selection.business_partner_id,
        isEnabled: selection.is_enabled || false,
        roles: selection.roles || [],
        priority: selection.priority || 0,
        customName: selection.custom_name || undefined,
        customNotes: selection.custom_notes || undefined,
        billingAllowed: selection.billing_allowed || false,
        pickupAllowed: selection.pickup_allowed || false,
        deliveryAllowed: selection.delivery_allowed || false,
        usageCount: selection.usage_count || 0,
        lastUsedAt: selection.last_used_at || null,
        createdAt: selection.created_at,
        updatedAt: selection.updated_at,
        createdBy: selection.created_by || undefined,
      }));

      logger.info(
        `✅ SupabasePartnerService: getCompanyPartnerSelections完了 - ${partnerSelections.length}件取得`
      );
      return partnerSelections;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: getCompanyPartnerSelections失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい会社取引先選択設定を作成
   * @param selectionData 選択設定データ（IDなし）
   * @returns 作成された選択設定データ
   */
  async createCompanyPartnerSelection(
    selectionData: Omit<CompanyPartnerSelection, 'id'>
  ): Promise<CompanyPartnerSelection> {
    logger.info('🚀 SupabasePartnerService: createCompanyPartnerSelection開始');
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_partner_selections')
        .insert([
          {
            company_id: selectionData.companyId,
            business_partner_id: selectionData.businessPartnerId,
            is_enabled: selectionData.isEnabled,
            roles: selectionData.roles,
            billing_allowed: selectionData.billingAllowed,
            pickup_allowed: selectionData.pickupAllowed,
            delivery_allowed: selectionData.deliveryAllowed,
            last_used_at: selectionData.lastUsedAt,
            usage_count: selectionData.usageCount,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `取引先選択設定の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      const selections = await this.getCompanyPartnerSelections(selectionData.companyId);
      const createdSelection = selections.find(s => s.id === data.id);
      if (!createdSelection) {
        throw new DataServiceError('作成された選択設定が見つかりません', ErrorCodes.UNKNOWN_ERROR);
      }

      logger.info(
        `✅ SupabasePartnerService: createCompanyPartnerSelection完了 - ${createdSelection.id}`
      );
      return createdSelection;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: createCompanyPartnerSelection失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択設定の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // CompanyPartnerSelection関連メソッド
  // =============================================================================

  /**
   * 会社取引先設定を取得
   * @param companyId 会社ID
   * @returns 取引先設定またはnull
   */
  async getCompanyPartnerSelection(companyId: string): Promise<CompanyPartnerSelection | null> {
    logger.info(
      `📋 SupabasePartnerService: getCompanyPartnerSelection開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_partner_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `取引先設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      // TODO: Fix this - CompanyPartnerSelection structure doesn't match legacy settings
      const settings: CompanyPartnerSelection = {
        id: data.id,
        companyId: data.company_id,
        businessPartnerId: data.business_partner_id || '',
        isEnabled: data.is_enabled || true,
        roles: data.roles || [],
        priority: data.priority || 0,
        billingAllowed: data.billing_allowed || false,
        pickupAllowed: data.pickup_allowed || false,
        deliveryAllowed: data.delivery_allowed || false,
        usageCount: data.usage_count || 0,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabasePartnerService: getCompanyPartnerSelection完了`);
      return settings;
    } catch (error) {
      logger.error('❌ SupabasePartnerService: getCompanyPartnerSelection失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  // =============================================================================
  // レガシー互換性メソッド（DEPRECATED）
  // =============================================================================

  /**
   * BusinessPartnerからClient形式へ変換（廃止予定）
   * @deprecated MasterDataContext.getClientById()を使用してください
   */
  async getClientsFromBusinessPartners(): Promise<Client[]> {
    logger.warn(
      '[DEPRECATED] getClientsFromBusinessPartners() - Use MasterDataContext.getClientById() instead'
    );
    return [];
  }

  /**
   * Client作成（廃止予定）
   * @deprecated BusinessPartner管理を使用してください
   */
  async createClientFromBusinessPartner(_clientData: Omit<Client, 'id'>): Promise<Client> {
    throw new DataServiceError(
      'DEPRECATED: Use BusinessPartner management instead',
      ErrorCodes.DEPRECATED_METHOD
    );
  }
}
